import { resetApiState } from '@/app/store/api/baseApi.ts';
import { setActiveMenu } from '@/app/store/slices/activeMenuSlice.ts';
import {
    clearServicePointProfile,
    setServicePointProfile,
} from '@/app/store/slices/servicePointProfileSlice.ts';
import { persistStateSnapshotNow } from '@/app/store/stateSnapshot/stateSnapshotPersistence.ts';
import { clearScanList } from '@/features/courierHandover/slices/courierHandoverSlice.ts';
import { resetAccordion } from '@/features/firstMile/slices/activeAccordionSlice.ts';
import {
    clearPendingRedirect,
    resetQuotationAndShippingDetails,
} from '@/features/firstMile/slices/quotationAndShippingSlice.ts';
import { clearShipmentSelection } from '@/features/firstMile/slices/shipmentSelectionSlice.ts';
import { resetCustomerCollection } from '@/features/lastMile/slices/customerCollectionSlice.ts';

import type { ServicePointProfileState } from '@/app/store/slices/servicePointProfileSlice.ts';
import type { Middleware } from '@reduxjs/toolkit';

interface ServicePointContextState {
    servicePointProfile: ServicePointProfileState;
    quotationAndShipping: {
        pendingRedirectPageId?: string;
    };
}

/**
 * Identity of the active service-point context. Uses the facility id when one is
 * selected, otherwise the country — so a country-only switch counts as a change
 * while re-hydrating the same service point with its full profile does not.
 */
export const getServicePointContextId = (
    state: Pick<ServicePointContextState, 'servicePointProfile'>,
): string => {
    const profile = state.servicePointProfile.profile;
    const servicePointId = profile?.srvPntFcId ?? '';
    if (servicePointId) return `sp:${servicePointId}`;
    return `country:${profile?.ctryCd ?? profile?.srvPntCtryCd ?? ''}`;
};

/**
 * Handles service-point context changes in one place, including updates received
 * from another tab:
 *
 * 1. Invalidate RTK Query data when a service-point profile is set.
 * 2. Reset Redux-backed operational forms when the service-point/country context
 *    actually changes. MainLayout's servicePointKey remount also resets page-local
 *    form state.
 * 3. If an edit has a source page, navigate there before the keyed page remount
 *    can interrupt the redirect.
 * 4. Persist the updated shell snapshot so a duplicated tab restores the latest
 *    service point.
 *
 * Menu navigation remains local to each tab; only the service-point profile is
 * broadcast.
 */
export const servicePointChangeMiddleware: Middleware =
    ({ dispatch, getState }) =>
    (next) =>
    (action) => {
        const isSet = setServicePointProfile.match(action);
        const isClear = clearServicePointProfile.match(action);
        if (!isSet && !isClear) return next(action);

        const previousContextId = getServicePointContextId(getState() as ServicePointContextState);
        const result = next(action);
        const nextContextId = getServicePointContextId(getState() as ServicePointContextState);
        const pendingRedirectPageId = (getState() as ServicePointContextState).quotationAndShipping
            .pendingRedirectPageId;

        if (isSet) {
            // Discard API data that may have been fetched for the previous service point.
            dispatch(resetApiState());
        }

        if (previousContextId !== nextContextId) {
            // Clear operational Redux forms only when the selected point or country changed.
            dispatch(resetQuotationAndShippingDetails());
            dispatch(resetAccordion());
            dispatch(clearShipmentSelection());
            dispatch(resetCustomerCollection());
            dispatch(clearScanList());
        }

        if (pendingRedirectPageId) {
            // Consume the edit source before MainLayout remounts the page for the new context.
            dispatch(clearPendingRedirect());
            dispatch(setActiveMenu(pendingRedirectPageId));
        }

        // Persist after the profile and form state updates so a duplicated tab restores current data.
        persistStateSnapshotNow();

        return result;
    };
